import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import api, { ApiError } from '../lib/apiClient';
import { IconButton } from '../components/IconButton';
import { IconDownload, IconEdit, IconTrash, IconView } from '../components/icons';
import Pagination from '../components/Pagination';
import { useAuth } from '../auth/useAuth';
import { useConfirm } from '../context/ConfirmContext';
import { useToast } from '../context/ToastContext';
import { downloadListCsv } from '../utils/csv';
import usePaginatedList from '../hooks/usePaginatedList';
import useCsvExport from '../hooks/useCsvExport';

// TS port of the source (Falah) app's pages/Customers.jsx. Route namespace
// is `/revenue/customers/...` here (not the source's `/billing/customers`)
// to match the path Pass A's UserDetail.tsx already assumed for this
// module — see that file's ASSOCIATIONS table.
interface CustomerRow {
  id: number;
  name: string;
  phone: string | null;
  email: string | null;
  quotations_count: number;
  invoices_count: number;
}

export function Customers() {
  const { can } = useAuth();
  const confirmDelete = useConfirm();
  const { showError } = useToast();
  const [search, setSearch] = useState('');
  const canManage = can('revenue.customers.manage');

  const filters = useMemo(() => ({ search: search || undefined }), [search]);
  const { data, loading, error, setPage, reload: load } = usePaginatedList<CustomerRow>('/customers', filters);

  const remove = async (customer: CustomerRow) => {
    if (!(await confirmDelete(`Delete customer "${customer.name}"?`))) return;
    try {
      await api.delete(`/customers/${customer.id}`);
      load();
    } catch (err) {
      showError(err instanceof ApiError ? err.message : 'Delete failed.');
    }
  };

  const { exporting, exportCsv } = useCsvExport(() =>
    downloadListCsv<CustomerRow>(
      api,
      '/customers',
      { search: search || undefined },
      [
        { label: 'Name', value: (r) => r.name },
        { label: 'Phone', value: (r) => r.phone ?? '' },
        { label: 'Email', value: (r) => r.email ?? '' },
        { label: 'Quotations', value: (r) => r.quotations_count },
        { label: 'Invoices', value: (r) => r.invoices_count },
      ],
      'customers.csv'
    )
  );

  return (
    <>
      <header className="page-header">
        <h1>Customers</h1>
        {canManage && (
          <Link className="btn btn-primary" to="/revenue/customers/new">
            + Add customer
          </Link>
        )}
      </header>

      <div className="toolbar">
        <input
          type="search"
          placeholder="Search name, phone, or email…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <IconButton
          icon={IconDownload}
          label={exporting ? 'Exporting…' : 'Download CSV'}
          onClick={exportCsv}
          disabled={exporting}
        />
      </div>

      <div className="card">
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Contact</th>
                <th className="right">Quotations</th>
                <th className="right">Invoices</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={5} className="muted center">
                    Loading…
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan={5} className="error center">
                    {error}
                  </td>
                </tr>
              ) : data && data.data.length > 0 ? (
                data.data.map((c) => (
                  <tr key={c.id}>
                    <td>
                      <strong>{c.name}</strong>
                    </td>
                    <td>
                      {c.email && <div>{c.email}</div>}
                      {c.phone && <small className="muted">{c.phone}</small>}
                      {!c.email && !c.phone && '—'}
                    </td>
                    <td className="right">{c.quotations_count ?? 0}</td>
                    <td className="right">{c.invoices_count ?? 0}</td>
                    <td className="right">
                      <IconButton icon={IconView} label="View" to={`/revenue/customers/${c.id}`} />
                      {canManage && (
                        <>
                          <IconButton icon={IconEdit} label="Edit" to={`/revenue/customers/${c.id}/edit`} />
                          <IconButton
                            icon={IconTrash}
                            label="Delete"
                            variant="danger-ghost"
                            onClick={() => remove(c)}
                          />
                        </>
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={5} className="muted center">
                    No customers yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <Pagination meta={data} onPage={setPage} />
      </div>
    </>
  );
}
